import { useTranslation } from 'react-i18next';

import { Button } from 'components/Button';
import { Loader } from 'components/Loader';

import { formatSeasonLabel } from 'helpers/formatSeason';

import { useDetails } from './Details.hooks';
import * as S from './Details.styles';

export function Details(): React.ReactElement {
  const { data, language, isLoading, downloadPDF, isLoadingPDF } = useDetails();
  const { t } = useTranslation();

  const products = data?.products ?? [];

  return (
    <Loader isLoading={isLoading}>
      {data?.title ? (
        <S.Header>
          <S.Title scale="xl3" textTransform="uppercase" fontWeight="normal" isWrapped={true}>
            {language === 'en' ? data.title.en : data.title.it}
          </S.Title>
          <S.Subtitle>
            <S.SeasonInfo>
              {t('catalogue.season')}: {formatSeasonLabel(data.season)} — {data.seasonLine}
            </S.SeasonInfo>
            {data.category && (
              <S.SeasonInfo>
                {t('catalogue.category')}:{' '}
                {language === 'en' ? data.category.nameEn : data.category.nameIt}
              </S.SeasonInfo>
            )}
            {data.category?.subCategories && data.category.subCategories.length > 0 && (
              <S.SeasonInfo>
                {t('catalogue.subCategory')}:{' '}
                {data.category.subCategories
                  .map((sub) => (language === 'en' ? sub.nameEn : sub.nameIt))
                  .join(', ')}
              </S.SeasonInfo>
            )}
          </S.Subtitle>
        </S.Header>
      ) : undefined}

      {data?.description ? (
        <S.Content
          dangerouslySetInnerHTML={{
            __html: language === 'en' ? data.description.en : data.description.it,
          }}
        />
      ) : undefined}

      {products.length > 0 ? (
        <>
          <S.ProductCount>
            {products.length}{' '}
            {products.length === 1 ? t('product.label_one') : t('product.label_other')}
          </S.ProductCount>
          {products.map((product) => (
            <S.ProductCard key={product.id}>
              <S.ProductMeta>
                <S.MetaItem>
                  <S.MetaLabel>{t('catalogue.itemCode')}</S.MetaLabel>
                  <S.MetaValue>{product.itemCode}</S.MetaValue>
                </S.MetaItem>
                <S.MetaItem>
                  <S.MetaLabel>{t('catalogue.season')}</S.MetaLabel>
                  <S.MetaValue>{formatSeasonLabel(product.season)}</S.MetaValue>
                </S.MetaItem>
                <S.MetaItem>
                  <S.MetaLabel>{t('catalogue.seasonLine')}</S.MetaLabel>
                  <S.MetaValue>{product.seasonLine}</S.MetaValue>
                </S.MetaItem>
                <S.MetaItem>
                  <S.MetaLabel>{t('catalogue.fabricCode')}</S.MetaLabel>
                  <S.MetaValue>{product.mainFabricCode}</S.MetaValue>
                </S.MetaItem>
                <S.MetaItem>
                  <S.MetaLabel>{t('catalogue.colorCode')}</S.MetaLabel>
                  <S.MetaValue>{product.itemColorCode}</S.MetaValue>
                </S.MetaItem>
              </S.ProductMeta>
              {(language === 'en' ? product.description.en : product.description.it) && (
                <S.ProductDescription>
                  {language === 'en' ? product.description.en : product.description.it}
                </S.ProductDescription>
              )}
            </S.ProductCard>
          ))}
        </>
      ) : undefined}

      <S.CTA>
        <Button
          isLoading={isLoadingPDF}
          onClick={() => downloadPDF()}
          aria-label={t('pdf.download')}
          fontFamily="LFT Zeno"
          scale="xs"
          motive="actionTertiary"
        >
          {t('pdf.download')}
        </Button>
      </S.CTA>
    </Loader>
  );
}
